import { useState, useCallback, useEffect } from 'react'
import Joyride, { Step, CallBackProps, STATUS, EVENTS } from 'react-joyride'
import { useAdminStore } from '@/store/adminStore'
import { useTranslation } from 'react-i18next'

// Custom content wrapper for tour steps
const TourContent = ({ title, content }: { title: string; content: string }) => (
  <div className="tour-content flex items-start gap-4 p-2">
    <div className="flex-1">
      <h2 className="text-lg font-semibold mb-2 text-[#525FE1]">{title}</h2>
      <p className="text-gray-600 text-sm leading-relaxed">{content}</p>
    </div>
  </div>
)

export const DatastorageAdminTour = () => {
  const [stepIndex, setStepIndex] = useState(0)
  const { t } = useTranslation('tour')

  const {
    datastorageTour,
    completeDatastorageTour,
    setDatastorageStep,
    nextDatastorageStep,
  } = useAdminStore()
  const [isRunning, setIsRunning] = useState(false)
  
  // Start tour when datastorageTour.isActive changes
  useEffect(() => {
    if (datastorageTour.isActive && !isRunning) {
      setIsRunning(true)
      setStepIndex(0)
      setDatastorageStep(0)
    } else if (!datastorageTour.isActive && isRunning) {
      setIsRunning(false)
      setStepIndex(0)
    }
  }, [datastorageTour.isActive, isRunning, setDatastorageStep])
  //  if (finishedStatuses.includes(status)) {
  //      setIsRunning(false)
  // Note: Tour will only start when explicitly activated via store (no auto-start here)

  // Define tour steps
  const steps: Step[] = [
    {
      target: '[data-tour="database-overview"]',
      content: <TourContent 
        title={t('admin.datastorageAdminTour.steps.databaseOverview.title')}
        content={t('admin.datastorageAdminTour.steps.databaseOverview.content')}
      />,
      disableBeacon: true,
      placement: 'center',
      spotlightPadding: 10,
      styles: {
        options: {
          zIndex: 10000
        }
      }
    },
    {
      target: '[data-tour="stats-cards"]',
      content: <TourContent 
        title={t('admin.datastorageAdminTour.steps.statsCards.title')}
        content={t('admin.datastorageAdminTour.steps.statsCards.content')}
      />,
      disableBeacon: true,
      placement: 'bottom',
      spotlightPadding: 5,
      styles: {
        options: {
          zIndex: 10000
        }
      }
    },
    {
      target: '[data-tour="recent-activities"]',
      content: <TourContent 
        title={t('admin.datastorageAdminTour.steps.recentActivities.title')}
        content={t('admin.datastorageAdminTour.steps.recentActivities.content')}
      />,
      disableBeacon: true,
      placement: 'bottom',
      spotlightPadding: 5,
      styles: {
        options: {
          zIndex: 10000
        }
      }
    },
    {
      target: '[data-tour="system-health"]',
      content: <TourContent 
        title={t('admin.datastorageAdminTour.steps.systemHealth.title')}
        content={t('admin.datastorageAdminTour.steps.systemHealth.content')}
      />,
      disableBeacon: true,
      placement: 'bottom',
      spotlightPadding: 2,
      styles: {
        options: {
          zIndex: 10000
        }
      }
    }
  ]

  // Handle tour callbacks
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { action, index, status, type } = data
// console.logDatastorage Tour callback:', { action, index, status, type })

    // End tour if finished or skipped
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
  // console.logDatastorage Tour ended')
      setIsRunning(false)
      setStepIndex(0)
      completeDatastorageTour()
      return
    }

    // Handle step navigation
    if (type === EVENTS.STEP_AFTER) {
      if (action === 'next') {
        const nextStepIndex = index + 1
        if (nextStepIndex < steps.length) {
      // console.logDatastorage Tour: Moving to next step', nextStepIndex)
          setStepIndex(nextStepIndex)
          setDatastorageStep(nextStepIndex)
          nextDatastorageStep()
        } else {
          // Last step completed
      // console.logDatastorage Tour: Last step completed')
          setIsRunning(false)
          setStepIndex(0)
          completeDatastorageTour()
        }
      } else if (action === 'prev' && index > 0) {
        const prevStepIndex = index - 1
    // console.logDatastorage Tour: Moving to previous step', prevStepIndex)
        setStepIndex(prevStepIndex)
        setDatastorageStep(prevStepIndex)
      }
    }

    // Update step index when step changes
    if (type === EVENTS.STEP_BEFORE || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index)
      setDatastorageStep(index)
    }

    // Ensure proper scroll position for each step
    if (type === EVENTS.STEP_BEFORE && action !== 'close') {
      const element = document.querySelector(steps[index]?.target as string)
      if (element) {
    // console.logScrolling to element:', steps[index]?.target)
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 100)
      } else {
        console.warn('Target element not found:', steps[index]?.target)
      }
    }
  }, [completeDatastorageTour, setDatastorageStep, nextDatastorageStep, steps.length])

  // No auto-start side effects

  return (
    <>
    <Joyride
      disableOverlayClose={true}
      callback={handleJoyrideCallback}
      continuous
      hideCloseButton
      hideBackButton
      scrollToFirstStep={true}
      disableScrollParentFix={true}
      showProgress
      showSkipButton
      steps={steps}
      run={isRunning}
      stepIndex={stepIndex}
      styles={{
        options: {
          primaryColor: '#ffffff',
          zIndex: 10000,
          arrowColor: '#ffffff',
          backgroundColor: '#ffffff',
        },
        buttonNext: {
          backgroundColor: '#ffffff',
          color: '#525FE1',
          fontSize: '16px',
          padding: '10px 20px',
          borderRadius: '6px',
        },
        buttonBack: {
          color: '#666',
          fontSize: '16px',
          padding: '10px',
          marginRight: '12px',
        },
        buttonSkip: {
          color: '#666',
          fontSize: '16px',
          padding: '10px',
        },
        tooltip: {
          padding: '24px',
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          color: '#333',
          fontSize: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxWidth: '500px',
          minWidth: '300px',
          pointerEvents: 'auto' as const,
          transition: 'opacity 0.2s ease',
        },
        tooltipContainer: {
          textAlign: 'left' as const,
        },
        spotlight: {
          backgroundColor: 'transparent', // Completely transparent
          border: '2px solid white', // White border
          borderRadius: '8px',
          boxShadow: 'none', // Remove box shadow since we handle it in CSS
        },
      }}
      locale={{
        back: t('admin.datastorageAdminTour.locale.back'),
        close: t('admin.datastorageAdminTour.locale.close'),
        last: t('admin.datastorageAdminTour.locale.last'),
        next: t('admin.datastorageAdminTour.locale.next'),
        skip: t('admin.datastorageAdminTour.locale.skip'),
      }}
    />
    
    {/* Dark overlay for consistent darkening */}
    {isRunning && datastorageTour.currentStep === 0 && (
      <div 
        className="fixed inset-0 bg-black/70 z-[9999] pointer-events-none"
        style={{ transition: 'opacity 0.2s ease' }}
      />
    )}
  </>
  )
} 